import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Brain, 
  ArrowLeft,
  Play,
  Trophy,
  Coins,
  RotateCcw,
  Video,
  Sparkles,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

type GameState = "idle" | "playing" | "finished";

interface Card {
  id: number;
  emoji: string;
  isFlipped: boolean;
  isMatched: boolean;
}

const EMOJIS = ["🎮", "🎯", "🎪", "🎨", "🎭", "🎰", "🎲", "🎸"];

const MemoryMatchGame: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [gameState, setGameState] = useState<GameState>("idle");
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [moves, setMoves] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [earnedATH, setEarnedATH] = useState(0);
  const [isClaimingReward, setIsClaimingReward] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [bonusMultiplier, setBonusMultiplier] = useState(1);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [showAdGate, setShowAdGate] = useState(false);
  const [adGateLoading, setAdGateLoading] = useState(false);

  // Check daily play count on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_memory_match_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    if (plays >= 1) {
      setShowAdGate(true);
    }
  }, [user?.id]);

  // Shuffle cards
  const shuffleCards = useCallback(() => {
    const pairs = EMOJIS.flatMap((emoji, idx) => [
      { id: idx * 2, emoji, isFlipped: false, isMatched: false },
      { id: idx * 2 + 1, emoji, isFlipped: false, isMatched: false },
    ]);
    return pairs.sort(() => Math.random() - 0.5);
  }, []);

  // Game timer
  useEffect(() => {
    if (gameState === "playing" && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (gameState === "playing" && timeLeft === 0) {
      endGame(false);
    }
  }, [gameState, timeLeft]);

  // Check for match when 2 cards are flipped
  useEffect(() => {
    if (flippedCards.length === 2) {
      setIsLocked(true);
      const [first, second] = flippedCards;
      const card1 = cards.find(c => c.id === first);
      const card2 = cards.find(c => c.id === second);

      if (card1 && card2 && card1.emoji === card2.emoji) {
        // Match found!
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            c.id === first || c.id === second ? { ...c, isMatched: true } : c
          ));
          setMatchedPairs(p => p + 1);
          setFlippedCards([]);
          setIsLocked(false);
        }, 500);
      } else {
        // No match - flip back
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            c.id === first || c.id === second ? { ...c, isFlipped: false } : c
          ));
          setFlippedCards([]);
          setIsLocked(false);
        }, 1000);
      }
      setMoves(m => m + 1);
    }
  }, [flippedCards, cards]);

  // Check for win
  useEffect(() => {
    if (gameState === "playing" && matchedPairs === EMOJIS.length) {
      endGame(true);
    }
  }, [matchedPairs, gameState]);

  const handleAdGateWatch = async () => {
    setAdGateLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user?.id || '', () => {
        setShowAdGate(false);
      });
    } catch (error) {
      console.error('Ad gate error:', error);
    } finally {
      setAdGateLoading(false);
    }
  };

  const incrementPlayCount = () => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_memory_match_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, (plays + 1).toString());
  };

  const startGame = () => {
    incrementPlayCount();
    setCards(shuffleCards());
    setGameState("playing");
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setTimeLeft(60);
    setEarnedATH(0);
    setShowReward(false);
    setIsLocked(false);
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const handleCardClick = (cardId: number) => {
    if (gameState !== "playing" || isLocked) return;
    
    const card = cards.find(c => c.id === cardId);
    if (!card || card.isFlipped || card.isMatched) return;
    if (flippedCards.length >= 2) return;

    setCards(prev => prev.map(c => 
      c.id === cardId ? { ...c, isFlipped: true } : c
    ));
    setFlippedCards(prev => [...prev, cardId]);
  };

  const endGame = (won: boolean) => {
    setGameState("finished");
    
    // Calculate ATH reward (reduced to match base mining rate of 0.7)
    let reward = 0.1; // Base reward for losing
    if (won) {
      // Bonus for winning
      reward = 1;
      // Extra bonus for fewer moves
      if (moves <= 12) reward = 2;
      else if (moves <= 16) reward = 1.5;
      // Time bonus
      if (timeLeft >= 30) reward += 0.3;
      else if (timeLeft >= 15) reward += 0.1;
    } else {
      // Partial reward based on matches (0.05 per match)
      reward = matchedPairs * 0.05;
    }
    
    setEarnedATH(parseFloat(reward.toFixed(2)));

    // Interstitial converted to Rewarded Ad for higher payout, triggered after game ends
    setTimeout(() => {
      if (user?.id) piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
    }, 2000);
  };

  const watchAdForBonus = async () => {
    if (!user?.id || isWatchingAd) return;
    
    setIsWatchingAd(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
      // If successful, double the reward
      setBonusMultiplier(2);
      setAdWatched(true);
      // Record that this ad was watched for a game bonus
      await arthereumProvider.recordGameAdBonus(user.id, "memory_match", earnedATH);
    } catch (error) {
      console.error("Ad failed:", error);
    } finally {
      setIsWatchingAd(false);
    }
  };

  const claimReward = async () => {
    if (!user?.id || isClaimingReward) return;
    
    const finalReward = parseFloat((earnedATH * bonusMultiplier).toFixed(2));
    
    setIsClaimingReward(true);
    try {
      await arthereumProvider.awardGameATH(user.id, "memory_match", finalReward, matchedPairs);
      
      // Update Redux locally instead of re-fetching from Firestore
      dispatch(actions.setActualUser({
        ...userData,
        user: {
          ...user,
          arthereumBalance: (user.arthereumBalance || 0) + finalReward,
        },
        miningInfos: userData.miningInfos,
      }));
      
      setShowReward(true);
    } catch (error: any) {
      alert(error.message || "Failed to claim reward");
    } finally {
      setIsClaimingReward(false);
    }
  };

  const playAgain = () => {
    setGameState("idle");
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const finalReward = earnedATH * bonusMultiplier;

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      {showAdGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-4 shadow-2xl">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <Video size={32} className="text-amber-500" />
            </div>
            <h3 className="text-xl font-black text-gray-900">Watch Ad to Play Again!</h3>
            <p className="text-sm text-gray-500">Your free daily play has been used. Watch a short ad to unlock another round!</p>
            <button
              onClick={handleAdGateWatch}
              disabled={adGateLoading}
              className="w-full py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg active:scale-95 transition-all"
            >
              {adGateLoading ? 'Loading Ad...' : '🎬 Watch Ad & Play!'}
            </button>
          </div>
        </div>
      )}
      <CommonPageTitle title="Memory Match" padding={null} />
      
      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(PAGE_NAME.GAMES)}
              className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
            >
              <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
            </button>
            <h1 className={`text-xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              Memory Match 🧠
            </h1>
          </div>
        </div>

        {/* Game Container */}
        <div className={`
          rounded-3xl p-4 text-center
          ${theme === "dark" 
            ? "bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30" 
            : "bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-300"}
        `}>
          {/* Stats Bar */}
          {gameState !== "idle" && (
            <div className="flex justify-around mb-4">
              <div className={`px-3 py-1 rounded-lg ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                <p className={`text-[10px] ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase`}>Time</p>
                <p className={`text-xl font-black ${timeLeft <= 10 ? "text-red-500 animate-pulse" : theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  {timeLeft}s
                </p>
              </div>
              <div className={`px-3 py-1 rounded-lg ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                <p className={`text-[10px] ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase`}>Moves</p>
                <p className={`text-xl font-black ${theme === "dark" ? "text-purple-400" : "text-purple-600"}`}>
                  {moves}
                </p>
              </div>
              <div className={`px-3 py-1 rounded-lg ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                <p className={`text-[10px] ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase`}>Pairs</p>
                <p className={`text-xl font-black ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"}`}>
                  {matchedPairs}/{EMOJIS.length}
                </p>
              </div>
            </div>
          )}

          {/* Game Area */}
          <AnimatePresence mode="wait">
            {gameState === "idle" && (
              <motion.div
                key="idle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Brain size={64} className="mx-auto text-purple-400 mb-4" />
                  <h2 className={`text-xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    How to Play
                  </h2>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                    Find all 8 matching pairs before time runs out!
                    <br />Fewer moves = more ATH rewards.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className={`text-xs ${theme === "dark" ? "text-slate-500" : "text-gray-400"}`}>Rewards:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>Lose: 0.05 ATH/match</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-purple-900/50 text-purple-400" : "bg-purple-100 text-purple-600"}`}>Win: 1-2 ATH</span>
                  </div>
                </div>

                <button
                  onClick={startGame}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-500/30 transition-all active:scale-95"
                >
                  <Play size={24} /> Start Game
                </button>
              </motion.div>
            )}

            {gameState === "playing" && (
              <motion.div
                key="playing"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <div className="grid grid-cols-4 gap-2">
                  {cards.map((card) => (
                    <motion.button
                      key={card.id}
                      onClick={() => handleCardClick(card.id)}
                      whileTap={{ scale: 0.95 }}
                      className={`
                        aspect-square rounded-xl text-2xl font-bold
                        flex items-center justify-center
                        transition-all duration-300
                        ${card.isFlipped || card.isMatched
                          ? theme === "dark" ? "bg-purple-600" : "bg-purple-400"
                          : theme === "dark" ? "bg-slate-700 hover:bg-slate-600" : "bg-white hover:bg-gray-100"
                        }
                        ${card.isMatched ? "opacity-50" : ""}
                      `}
                    >
                      {(card.isFlipped || card.isMatched) ? card.emoji : "?"}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {gameState === "finished" && (
              <motion.div
                key="finished"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Trophy size={64} className="mx-auto text-purple-400 mb-4" />
                  <h2 className={`text-2xl font-black mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {matchedPairs === EMOJIS.length ? "You Won! 🎉" : "Time's Up!"}
                  </h2>
                  <p className={`text-lg ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                    {matchedPairs}/{EMOJIS.length} pairs in {moves} moves
                  </p>
                </div>

                {/* Reward Display */}
                <div className={`p-4 rounded-2xl ${theme === "dark" ? "bg-emerald-900/30 border border-emerald-500/30" : "bg-emerald-100 border border-emerald-300"}`}>
                  <p className={`text-sm ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"} mb-1`}>You earned:</p>
                  <p className={`text-3xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"} flex items-center justify-center gap-2`}>
                    <Coins className="text-yellow-400" /> 
                    {finalReward.toFixed(2)} ATH
                    {bonusMultiplier > 1 && (
                      <span className="text-emerald-400 text-sm ml-1">({bonusMultiplier}x)</span>
                    )}
                  </p>
                </div>

                {/* Watch Ad for Bonus */}
                {!showReward && !adWatched && (
                  <motion.button
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    onClick={watchAdForBonus}
                    disabled={isWatchingAd}
                    className={`
                      w-full py-3 rounded-2xl flex items-center justify-center gap-2 font-bold
                      bg-gradient-to-r from-purple-600 to-indigo-600 text-white
                      hover:shadow-lg hover:shadow-purple-500/30 transition-all active:scale-95
                      disabled:opacity-50
                    `}
                  >
                    <Video size={20} />
                    {isWatchingAd ? "Loading Ad..." : (
                      <>
                        Watch Ad for <span className="text-emerald-300">2X BONUS</span>
                        <Sparkles size={16} className="text-yellow-300" />
                      </>
                    )}
                  </motion.button>
                )}

                {/* Bonus Applied Badge */}
                {adWatched && !showReward && (
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center text-sm font-bold">
                    ✨ 2X BONUS APPLIED!
                  </div>
                )}

                {!showReward ? (
                  <button
                    onClick={claimReward}
                    disabled={isClaimingReward}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isClaimingReward ? "Claiming..." : `Claim ${finalReward.toFixed(2)} ATH 🎁`}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className={`p-3 rounded-xl ${theme === "dark" ? "bg-emerald-500" : "bg-emerald-500"} text-white text-center font-bold`}>
                      ✅ Reward Claimed!
                    </div>
                    <button
                      onClick={playAgain}
                      className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg transition-all active:scale-95"
                    >
                      <RotateCcw size={20} /> Play Again
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default MemoryMatchGame;
